import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Wind, Mountain, Sparkles } from 'lucide-react';

const BANNER_BG = 'https://images.pexels.com/photos/37406898/pexels-photo-37406898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920';

const EASE = [0.16, 1, 0.3, 1];
const container = { hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } };
const up = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } } };

const CHIPS = [
  { Icon: Mountain, label: '1.928 m · Botte Donato' },
  { Icon: Wind, label: "L'aria più pura d'Europa" },
  { Icon: Sparkles, label: 'Riserva MaB UNESCO' },
];

// Polaroid sparse: foto + didascalia, rotazione e posizione diverse.
const POLAROIDS = [
  { src: 'https://images.pexels.com/photos/20450281/pexels-photo-20450281.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=420&w=420', caption: 'Lago Ampollino', rotate: -8, dur: 7.5, delay: 0, pos: 'left-[4%] top-[2%] z-20' },
  { src: 'https://images.pexels.com/photos/17983542/pexels-photo-17983542.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=420&w=420', caption: 'Boschi secolari', rotate: 7, dur: 9, delay: 0.6, pos: 'right-[2%] top-[20%] z-30' },
  { src: 'https://images.pexels.com/photos/28127038/pexels-photo-28127038.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=420&w=420', caption: 'Silenzio d\u2019alta quota', rotate: -4, dur: 8.2, delay: 1.1, pos: 'bottom-[2%] left-[24%] z-20' },
];

const Polaroid = ({ src, caption, rotate, dur, delay, pos, reduce }) => (
  <motion.figure
    className={`group/pol absolute w-32 rounded-[10px] bg-white p-2 pb-7 shadow-[0_18px_45px_-12px_rgba(0,0,0,0.6)] ring-1 ring-black/5 sm:w-40 lg:w-44 ${pos}`}
    initial={{ opacity: 0, y: 30, rotate }}
    animate={reduce ? { opacity: 1, rotate } : { opacity: 1, y: [0, -12, 0], rotate }}
    transition={reduce ? { duration: 0.6 } : { opacity: { duration: 0.7, delay }, y: { duration: dur, repeat: Infinity, ease: 'easeInOut', delay }, default: { duration: 0.7, delay } }}
    whileHover={{ rotate: 0, scale: 1.06, zIndex: 40, transition: { duration: 0.25, ease: EASE } }}
  >
    <div className="overflow-hidden rounded-[4px]">
      <img src={src} alt={caption} loading="lazy" className="h-28 w-full object-cover transition-transform duration-500 ease-out group-hover/pol:scale-110 sm:h-36 lg:h-40" />
    </div>
    <figcaption className="absolute inset-x-0 bottom-1.5 text-center font-display text-[11px] font-medium italic text-slate-700 sm:text-xs">{caption}</figcaption>
  </motion.figure>
);

export const SilaBanner = () => {
  const reduce = useReducedMotion();

  return (
    <section className="sila-section py-10 sm:py-14" data-testid="sila-banner">
      <div className="relative isolate overflow-hidden rounded-[28px] shadow-[var(--shadow-soft)] sm:rounded-[36px]">
        {/* Background + Ken Burns */}
        <motion.div aria-hidden className="absolute inset-0 -z-10" initial={reduce ? false : { scale: 1.12 }} animate={reduce ? {} : { scale: 1 }} transition={{ duration: 14, ease: 'easeOut' }}>
          <img src={BANNER_BG} alt="Altopiano della Sila" className="h-full w-full object-cover" loading="lazy" />
        </motion.div>
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(105deg, hsl(150 42% 10% / 0.94) 0%, hsl(150 42% 12% / 0.72) 46%, hsl(150 42% 14% / 0.30) 100%)' }} />
        <div aria-hidden className="grain-overlay absolute inset-0 -z-10 opacity-[0.5]" />
        {!reduce && (
          <>
            <motion.div aria-hidden className="pointer-events-none absolute -right-24 -top-24 -z-10 h-72 w-72 rounded-full blur-3xl" style={{ background: 'hsl(var(--brand-sun) / 0.30)' }} animate={{ y: [0, 26, 0], opacity: [0.5, 0.8, 0.5] }} transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }} />
            <motion.div aria-hidden className="pointer-events-none absolute -bottom-28 left-10 -z-10 h-64 w-64 rounded-full blur-3xl" style={{ background: 'hsl(150 60% 45% / 0.26)' }} animate={{ y: [0, -22, 0] }} transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut' }} />
          </>
        )}

        <div className="relative grid items-center gap-8 px-6 py-14 sm:px-10 md:px-14 md:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-6 lg:py-20">
          {/* Testo */}
          <motion.div className="text-white" variants={container} initial="hidden" animate="show">
            <motion.span variants={up} className="inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] backdrop-blur-md sm:text-xs">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'hsl(var(--brand-sun))' }} />
              Parco Nazionale della Sila · Calabria
            </motion.span>
            <motion.h2 variants={up} className="mt-5 max-w-2xl font-display text-[2rem] font-bold leading-[1.05] tracking-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)] sm:text-5xl lg:text-[3.4rem]">
              Dove il tempo si ferma
              <span className="block" style={{ color: 'hsl(var(--brand-sun))' }}>e i sensi si amplificano.</span>
            </motion.h2>
            <motion.p variants={up} className="mt-5 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg">
              Boschi secolari, laghi che sembrano mari e l'aria più pura d'Europa. La Sila non si
              visita: <strong className="font-semibold text-white">si vive</strong>. Immergiti in una
              realtà unica dove le emozioni sgorgano — e riscopri la tua natura attraverso la Natura.
            </motion.p>
            <motion.ul variants={up} className="mt-7 flex flex-wrap gap-2.5">
              {CHIPS.map(({ Icon, label }) => (
                <li key={label} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-[12px] font-medium backdrop-blur-md sm:text-sm">
                  <Icon className="h-4 w-4" style={{ color: 'hsl(var(--brand-sun))' }} />{label}
                </li>
              ))}
            </motion.ul>
            <motion.div variants={up} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="group h-12 rounded-full px-7 text-base shadow-lg" data-testid="banner-cta-lasila">
                <Link to="/la-sila">Scopri La Sila<ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 rounded-full border-white/40 bg-white/5 px-7 text-base text-white backdrop-blur-md hover:bg-white/15 hover:text-white" data-testid="banner-cta-partner">
                <Link to="/esperienze">Vivi un'esperienza</Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Polaroid cluster */}
          <motion.div
            className="relative mx-auto h-[300px] w-full max-w-[420px] sm:h-[360px] lg:h-[420px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            data-testid="banner-polaroids"
          >
            {POLAROIDS.map((p) => (
              <Polaroid key={p.caption} {...p} reduce={reduce} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SilaBanner;
